import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputAdornment,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material';
import { FormikProps } from 'formik';
import { InvoiceDetailsSettingsFormData } from 'models/forms';
import { FinnishReferenceFormat } from 'utils/payment-details-validation';

type FinnishField = 'latePaymentInterest' | 'complaintPeriodDays';

/**
 * DD-3700: a Finnish lab's payment reference format (viitenumero or RF) and the late payment
 * interest and complaint period printed on its invoices.
 */
const FinnishPaymentFields: FC<{ formik: FormikProps<InvoiceDetailsSettingsFormData> }> = ({ formik }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const fieldError = (field: FinnishField) => {
        const message = formik.touched[field] ? formik.errors[field] : undefined;
        return { error: Boolean(message), message };
    };
    const interestError = fieldError('latePaymentInterest');
    const complaintError = fieldError('complaintPeriodDays');

    return (
        <>
            <Grid item xs={12}>
                <FormControl>
                    <FormLabel id="reference-format-label">{translate('lab-settings:REFERENCE_FORMAT')}</FormLabel>
                    <RadioGroup
                        row
                        aria-labelledby="reference-format-label"
                        {...formik.getFieldProps('referenceFormat')}
                    >
                        <FormControlLabel
                            value={FinnishReferenceFormat.NATIONAL}
                            control={<Radio />}
                            label={translate('lab-settings:REFERENCE_FORMAT_NATIONAL')}
                        />
                        <FormControlLabel
                            value={FinnishReferenceFormat.RF}
                            control={<Radio />}
                            label={translate('lab-settings:REFERENCE_FORMAT_RF')}
                        />
                    </RadioGroup>
                </FormControl>
                <Typography variant="body2" color="text.secondary">
                    {translate('lab-settings:REFERENCE_FORMAT_INFO')}
                </Typography>
            </Grid>
            <Grid item sm={12} md={6}>
                <TextField
                    id="latePaymentInterest"
                    type="number"
                    label={translate('lab-settings:LATE_PAYMENT_INTEREST')}
                    fullWidth
                    inputProps={{ min: 0, max: 100, step: 0.5 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                    {...formik.getFieldProps('latePaymentInterest')}
                    error={interestError.error}
                    helperText={interestError.message || translate('lab-settings:LATE_PAYMENT_INTEREST_INFO')}
                />
            </Grid>
            <Grid item sm={12} md={6}>
                <TextField
                    id="complaintPeriodDays"
                    type="number"
                    label={translate('lab-settings:COMPLAINT_PERIOD')}
                    fullWidth
                    inputProps={{ min: 0, max: 365, step: 1 }}
                    InputProps={{
                        endAdornment: <InputAdornment position="end">{translate('lab-settings:DAYS')}</InputAdornment>,
                    }}
                    {...formik.getFieldProps('complaintPeriodDays')}
                    error={complaintError.error}
                    helperText={complaintError.message || translate('lab-settings:COMPLAINT_PERIOD_INFO')}
                />
            </Grid>
        </>
    );
};

export default FinnishPaymentFields;
